import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import toast from "react-hot-toast";
import { useAuth } from "../../hooks";
import { logout } from "../../store/auth/index";
import { API } from "../../store/api";
import { ButtonComp } from "../Ui/ButtonComp";

const WARNING_MS = 30_000;

function parseIdleTimeoutMs() {
  const raw = process.env.REACT_APP_IDLE_TIMEOUT_MINUTES;
  const parsed = parseFloat(raw);
  let minutes = Number.isFinite(parsed) && parsed > 0 ? parsed : 10;
  minutes = Math.max(1, minutes);
  return minutes * 60 * 1000;
}

/**
 * Logs the user out after configurable idle time; shows a 30s countdown before logout.
 * Only runs when a session token exists (admin/staff and member shells).
 */
export default function IdleSessionWatcher() {
  const token = useAuth();
  const dispatch = useDispatch();
  const idleMs = useMemo(() => parseIdleTimeoutMs(), []);

  const [showWarning, setShowWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(30);

  const timersRef = useRef({
    warning: null,
    logout: null,
    countdown: null,
  });
  const lastMoveRef = useRef(0);

  const clearAllTimers = useCallback(() => {
    const t = timersRef.current;
    if (t.warning) clearTimeout(t.warning);
    if (t.logout) clearTimeout(t.logout);
    if (t.countdown) clearInterval(t.countdown);
    t.warning = null;
    t.logout = null;
    t.countdown = null;
  }, []);

  const performIdleLogout = useCallback(() => {
    clearAllTimers();
    setShowWarning(false);
    dispatch(logout());
    dispatch(API.util.resetApiState());
    toast.error("You were logged out due to inactivity.");
  }, [clearAllTimers, dispatch]);

  const scheduleTimers = useCallback(() => {
    clearAllTimers();
    setShowWarning(false);

    const warnDelay = idleMs - WARNING_MS;
    const t = timersRef.current;

    t.warning = setTimeout(() => {
      const deadline = Date.now() + WARNING_MS;
      setShowWarning(true);
      setSecondsLeft(Math.ceil(WARNING_MS / 1000));

      t.countdown = setInterval(() => {
        const left = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
        setSecondsLeft(left);
        if (left <= 0 && t.countdown) {
          clearInterval(t.countdown);
          t.countdown = null;
        }
      }, 250);
    }, warnDelay);

    t.logout = setTimeout(performIdleLogout, idleMs);
  }, [clearAllTimers, idleMs, performIdleLogout]);

  useEffect(() => {
    if (!token) {
      clearAllTimers();
      setShowWarning(false);
      return undefined;
    }

    scheduleTimers();

    const onImmediate = () => scheduleTimers();
    const onMouseMove = () => {
      const now = Date.now();
      if (now - lastMoveRef.current < 1000) return;
      lastMoveRef.current = now;
      scheduleTimers();
    };

    window.addEventListener("keydown", onImmediate);
    window.addEventListener("click", onImmediate);
    window.addEventListener("touchstart", onImmediate, { passive: true });
    window.addEventListener("scroll", onImmediate, { passive: true });
    window.addEventListener("mousemove", onMouseMove);

    return () => {
      window.removeEventListener("keydown", onImmediate);
      window.removeEventListener("click", onImmediate);
      window.removeEventListener("touchstart", onImmediate);
      window.removeEventListener("scroll", onImmediate);
      window.removeEventListener("mousemove", onMouseMove);
      clearAllTimers();
      setShowWarning(false);
    };
  }, [token, scheduleTimers, clearAllTimers]);

  if (!showWarning) return null;

  return (
    <div
      role="alert"
      aria-live="polite"
      className="position-fixed bottom-0 start-0 end-0 d-flex justify-content-center px-3 pb-3"
      style={{ zIndex: 1080, pointerEvents: "none" }}
    >
      <div
        className="shadow-lg rounded-4 px-4 py-3 d-flex flex-column flex-md-row align-items-center gap-3"
        style={{
          pointerEvents: "auto",
          maxWidth: 640,
          width: "100%",
          background: "var(--color-primary-dark)",
          color: "#ffffff",
          border: "1px solid #e0e0e0",
        }}
      >
        <p className="mb-0 text-center text-md-start flex-grow-1" style={{ fontSize: 15 }}>
          You will be logged out in <strong>{secondsLeft}</strong> second
          {secondsLeft === 1 ? "" : "s"} due to inactivity.
        </p>
        <div style={{ minWidth: 200 }}>
          <ButtonComp
            type="button"
            btnText="Stay logged in"
            onClick={() => scheduleTimers()}
            btnClassName="btn w-100 py-2 rounded-3 fw-2 text-white border-0"
            ButtonInlineStyle={{
              backgroundColor: "var(--color-secondary)",
              minHeight: 44,
            }}
          />
        </div>
      </div>
    </div>
  );
}
